import type { SignalKey } from '@api/devices/contracts';
import { osValues, type OsValue, type RecordKind, statusValues, type StatusValue } from '@contracts';
import { type FilterState, SEEN_LIMITS, type SeenFilter, type Sort, type Unit } from '../search/state';

// Deterministic reading of operator questions into the shared typed filters. It
// never invents a field: words it cannot map are reported back as skipped. A
// model-backed interpreter can replace this later behind the same output.

export interface Catalog {
  organizations: Array<{ id: string; title: string }>;
  sites: Array<{ id: string; title: string; organizationId: string }>;
}
export interface Reading { text: string; meaning: string; type: 'entity' | 'filter' | 'sort' }
export interface Skipped { text: string; reason: string }
export type InterpretedFilters = Pick<FilterState, 'q' | 'kind' | 'org' | 'site' | 'os' | 'status' | 'seen' | 'sort' | 'signal' | 'software'> & { duplicateName?: boolean };
export interface Interpretation {
  input: string;
  filters: InterpretedFilters;
  readings: Reading[];
  skipped: Skipped[];
  /** At least one phrase became a structured filter, kind or sort. */
  understood: boolean;
  /** The user asked for a roll-up ("which customers have…"); results stay records. */
  groupBy: 'organization' | 'site' | null;
}

/** `pause`: a comma or other punctuation ends the clause before this token. */
interface Token { raw: string; norm: string; used: boolean; pause: boolean }

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const looksLikeIdentifier = (raw: string) => /\d|[-_.]/.test(raw) || (raw.length >= 3 && raw === raw.toUpperCase() && /[A-Z]/.test(raw));

const normalize = (word: string) => word.toLowerCase().replace(/[’‘]/g, "'").replace(/^['"]+|['".:]+$/g, '')
  .replace(/'s$/, '').replace(/^&$/, 'and');
const titleTokens = (title: string) => title.split(/\s+/).map(normalize).filter(Boolean);

const STOP = new Set(('a an the and or of to for from with without in on at by about is are was were be been being do does did ' +
  'have has had having i me my we us our you your they them their it its this that these those there here which what who whose ' +
  'where when how show list find get give see tell display all any every each some please can could would should will ' +
  'just only also still currently right now yet more than over under less fewer since ago last past within during anything ' +
  'everything something stuff thing things one ones whats what\'s let\'s lets look looking check checking up out across inside ' +
  'need needs needing many much count number').split(' '));

const NUMBER_WORDS: Record<string, number> = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, fourteen: 14, thirty: 30, couple: 2, few: 3, several: 3 };
const UNITS: Record<string, { unit: Unit; factor: number }> = {
  hour: { unit: 'h', factor: 1 }, hours: { unit: 'h', factor: 1 }, hr: { unit: 'h', factor: 1 }, hrs: { unit: 'h', factor: 1 },
  day: { unit: 'd', factor: 1 }, days: { unit: 'd', factor: 1 }, week: { unit: 'd', factor: 7 }, weeks: { unit: 'd', factor: 7 },
  wk: { unit: 'd', factor: 7 }, wks: { unit: 'd', factor: 7 }, month: { unit: 'd', factor: 30 }, months: { unit: 'd', factor: 30 },
};
const CONTACT = new Set(['seen', 'checked', 'check', 'checkin', 'check-in', 'checked-in', 'reported', 'report', 'reporting', 'online',
  'connected', 'contacted', 'synced', 'heard', 'responded', 'active', 'communicated', 'phoned', 'called']);
const NEGATION = new Set(['not', 'no', "haven't", 'havent', "hasn't", 'hasnt', "didn't", 'didnt', "isn't", 'isnt', "aren't", 'arent',
  "wasn't", 'wasnt', "weren't", 'werent', "don't", 'dont', "doesn't", 'doesnt', 'without']);

const STATUS_WORDS: Array<[string[], StatusValue[], string]> = [
  [['needs', 'attention'], ['attention'], 'Status is Attention'], [['need', 'attention'], ['attention'], 'Status is Attention'],
  [['needing', 'attention'], ['attention'], 'Status is Attention'], [['not', 'responding'], ['offline'], 'Status is Offline'],
  [['unknown', 'status'], ['unknown'], 'No status reported'], [['status', 'unknown'], ['unknown'], 'No status reported'],
  [['no', 'status'], ['unknown'], 'No status reported'],
  [['needs', 'a', 'look'], ['attention', 'offline', 'unknown'], 'Status is Attention, Offline or not reported'],
  [['need', 'a', 'look'], ['attention', 'offline', 'unknown'], 'Status is Attention, Offline or not reported'],
  [['at', 'risk'], ['attention', 'offline', 'unknown'], 'Status is Attention, Offline or not reported'],
  [['attention'], ['attention'], 'Status is Attention'], [['warning'], ['attention'], 'Status is Attention'],
  [['warnings'], ['attention'], 'Status is Attention'], [['alert'], ['attention'], 'Status is Attention'],
  [['alerts'], ['attention'], 'Status is Attention'], [['alerting'], ['attention'], 'Status is Attention'],
  [['degraded'], ['attention'], 'Status is Attention'], [['failing'], ['attention'], 'Status is Attention'],
  [['problems'], ['attention', 'offline'], 'Status is Attention or Offline'], [['problem'], ['attention', 'offline'], 'Status is Attention or Offline'],
  [['issues'], ['attention', 'offline'], 'Status is Attention or Offline'], [['issue'], ['attention', 'offline'], 'Status is Attention or Offline'],
  [['unhealthy'], ['attention', 'offline'], 'Status is Attention or Offline'], [['broken'], ['attention', 'offline'], 'Status is Attention or Offline'],
  [['wrong'], ['attention', 'offline'], 'Status is Attention or Offline'], [['troubled'], ['attention', 'offline'], 'Status is Attention or Offline'],
  [['offline'], ['offline'], 'Status is Offline'], [['down'], ['offline'], 'Status is Offline'],
  [['disconnected'], ['offline'], 'Status is Offline'], [['unreachable'], ['offline'], 'Status is Offline'],
  [['unresponsive'], ['offline'], 'Status is Offline'],
  [['healthy'], ['healthy'], 'Status is Healthy'], [['ok'], ['healthy'], 'Status is Healthy'], [['okay'], ['healthy'], 'Status is Healthy'],
];
const OS_WORDS: Array<[string[], OsValue, string | null]> = [
  [['windows', '10'], 'windows', 'Version isn’t a filter yet — for Windows 10 try “unsupported”'], [['windows', '11'], 'windows', 'Version isn’t a filter yet — try “support ending”'],
  [['unknown', 'os'], 'unknown', null], [['os', 'unknown'], 'unknown', null], [['no', 'os'], 'unknown', null],
  [['mac', 'os'], 'macos', null], [['os', 'x'], 'macos', null], [['mac', 'mini'], 'macos', 'Mac model isn’t recorded yet'],
  [['windows'], 'windows', null], [['win'], 'windows', null], [['win10'], 'windows', 'Windows version isn’t recorded yet'],
  [['win11'], 'windows', 'Windows version isn’t recorded yet'],
  [['mac'], 'macos', null], [['macs'], 'macos', null], [['macos'], 'macos', null], [['osx'], 'macos', null], [['apple'], 'macos', null],
  [['macbook'], 'macos', 'Mac model isn’t recorded yet'], [['macbooks'], 'macos', 'Mac model isn’t recorded yet'],
  [['imac'], 'macos', 'Mac model isn’t recorded yet'], [['imacs'], 'macos', 'Mac model isn’t recorded yet'],
  [['linux'], 'linux', null], [['ubuntu'], 'linux', 'Linux distribution isn’t recorded yet'],
  [['debian'], 'linux', 'Linux distribution isn’t recorded yet'], [['rhel'], 'linux', 'Linux distribution isn’t recorded yet'],
  [['centos'], 'linux', 'Linux distribution isn’t recorded yet'], [['fedora'], 'linux', 'Linux distribution isn’t recorded yet'],
];
const PROTECTION: SignalKey[] = ['security.av_off', 'security.av_stale', 'security.firewall_off', 'security.encryption_off', 'security.remote_inbound',
  'os.unsupported'];
const sig = (phrases: string[], keys: SignalKey[], meaning: string) => phrases.map(p => [p.split(' '), keys, meaning] as [string[], SignalKey[], string]);
// Health signals the agent can actually evidence. Longest phrases are matched first.
const SIGNAL_WORDS: Array<[string[], SignalKey[], string]> = [
  ...sig(['security issues', 'security problems', 'security gaps', 'security risks', 'protection gaps', 'protection issues', 'not protected'], PROTECTION, 'Has a protection or support gap'),
  ...sig(['running out of space', 'running out of disk', 'out of space', 'out of disk space', 'disk space', 'storage problems', 'storage issues'], ['disk.low', 'disk.filling'], 'Disk low or filling'),
  ...sig(['low disk space', 'low disk', 'low storage', 'disk full', 'full disk', 'disks full', 'full disks'], ['disk.low'], 'Low disk space'),
  ...sig(['filling up', 'disk filling', 'disks filling'], ['disk.filling'], 'Disk projected to fill'),
  ...sig(['memory pressure', 'high memory', 'low memory', 'out of memory', 'high ram', 'low ram'], ['memory.pressure'], 'Memory nearly full'),
  ...sig(['high cpu', 'cpu pressure', 'maxed cpu', 'cpu maxed'], ['cpu.sustained'], 'CPU saturated'),
  ...sig(['slow', 'sluggish'], ['cpu.sustained', 'memory.pressure'], 'CPU or memory pressure (slowness itself isn’t measured)'),
  ...sig(['pending reboot', 'pending restart', 'needs a reboot', 'need a reboot', 'needs a restart', 'need a restart', 'needs reboot', 'need reboot',
    'needs restart', 'need restart', 'reboot required', 'restart required', 'requires a reboot', 'requires a restart'], ['reboot.pending'], 'Restart pending'),
  ...sig(['antivirus off', 'antivirus disabled', 'av off', 'av disabled', 'defender off', 'defender disabled', 'no antivirus', 'without antivirus', 'without av', 'without defender', 'unprotected',
    'real-time protection off', 'realtime protection off'], ['security.av_off'], 'Antivirus not protecting'),
  ...sig(['old definitions', 'outdated definitions', 'stale definitions', 'definitions out of date', 'out of date definitions'], ['security.av_stale'], 'Antivirus definitions out of date'),
  ...sig(['firewall off', 'firewall disabled', 'no firewall', 'firewalls off'], ['security.firewall_off'], 'Firewall off'),
  ...sig(['remote reachable', 'remote exposed', 'remote open', 'open udp', 'inbound udp', 'meshagent listening'], ['security.remote_inbound'], 'WITTCO Remote reachable from the network'),
  ...sig(['not encrypted', 'unencrypted', 'bitlocker off', 'no bitlocker', 'encryption off', 'no encryption', 'filevault off', 'without encryption'], ['security.encryption_off'], 'Disk not encrypted'),
  ...sig(['end of life', 'end of support', 'out of support', 'unsupported', 'eol', 'no longer supported'], ['os.unsupported'], 'Operating system no longer supported'),
  ...sig(['support ending', 'ending support', 'support ends', 'feature update', 'need an upgrade', 'needs an upgrade', 'needs upgrading'], ['os.ending'], 'Operating system support ending soon'),
  ...sig(['missing updates', 'not patched', 'unpatched', 'updates overdue', 'behind on updates', 'out of date', 'outdated'], ['patch.stale'], 'Updates overdue'),
  ...sig(['updates paused', 'paused updates', 'update paused', 'paused update', 'windows update paused', 'paused windows update', 'paused windows updates'],
    ['patch.paused'], 'Windows Update paused'),
].sort((a, b) => b[0].length - a[0].length);
const OS_LABEL: Record<OsValue, string> = { windows: 'Windows', macos: 'macOS', linux: 'Linux', unknown: 'Unknown OS' };
const STATUS_LABEL: Record<StatusValue, string> = { healthy: 'Healthy', attention: 'Attention', offline: 'Offline', unknown: 'unreported' };
/** Words that may sit between a negation and what it negates: "not currently offline", "without a problem", "not an offline device". */
const NEGATION_FILLER = new Set(['a', 'an', 'the', 'currently', 'any', 'had', 'been', 'yet', 'showing', 'reporting']);
const KIND_WORDS: Array<[string, RecordKind, string | null]> = [
  ...['device', 'devices', 'computer', 'computers', 'machine', 'machines', 'endpoint', 'endpoints', 'pc', 'pcs', 'workstation',
    'workstations', 'host', 'hosts', 'fleet', 'assets', 'asset'].map(w => [w, 'device', null] as [string, RecordKind, null]),
  ...['laptop', 'laptops', 'desktop', 'desktops', 'server', 'servers', 'tablet', 'tablets', 'notebook', 'notebooks', 'kiosk', 'kiosks']
    .map(w => [w, 'device', 'Device type (laptop, desktop, server) isn’t recorded yet — showing all devices'] as [string, RecordKind, string]),
  ...['customer', 'customers', 'client', 'clients', 'organization', 'organizations', 'organisation', 'organisations', 'org', 'orgs',
    'company', 'companies', 'account', 'accounts'].map(w => [w, 'organization', null] as [string, RecordKind, null]),
  ...['site', 'sites', 'location', 'locations', 'office', 'offices', 'branch', 'branches'].map(w => [w, 'site', null] as [string, RecordKind, null]),
];
const PREPOSITIONS = new Set(['at', 'in', 'from', 'for', '@', 'of']);
// Search can't leave a customer or site out: "except Acme" must not become "Acme".
const EXCLUSION = new Set(['except', 'excepting', 'excluding', 'exclude', 'besides', 'minus', 'barring', 'sans', 'ignore', 'ignoring',
  'skip', 'skipping', 'omit', 'omitting']);
const EXCLUSION_PAIRS = new Map([['other', 'than'], ['apart', 'from'], ['aside', 'from']]);
/** "Everything but …", "all but …", "but those …": a "but" that carves something out rather than joining two conditions. */
const excludingBut = (tokens: Token[], i: number) => tokens[i]!.norm === 'but'
  && (['everything', 'all', 'anything', 'everyone', 'every'].includes(tokens[i - 1]?.norm ?? '') || ['those', 'ones', 'the'].includes(tokens[i + 1]?.norm ?? ''));
const OUTSIDE = 'Can’t search for devices outside a customer or site yet';
const PHRASES = [...STATUS_WORDS, ...OS_WORDS, ...SIGNAL_WORDS].map(([words]) => words);
const DEVICE_NOUNS = new Set(KIND_WORDS.map(([w]) => w));
/** "Offline but at Acme", "offline devices but at Acme": only a "but" right after a condition joins it to the next. */
const joiningBut = (tokens: Token[], i: number) => {
  let end = i;
  while (end > 0 && DEVICE_NOUNS.has(tokens[end - 1]!.norm)) end--;
  return PHRASES.some(w => w.length <= end && w.every((x, k) => tokens[end - w.length + k]!.norm === x));
};
const QUIET = ['stale', 'silent', 'quiet', 'dormant', 'inactive', 'missing', 'idle'];
// Words with their own meaning are never read as the start of a customer name.
const KEYWORDS = new Set([...STATUS_WORDS.flatMap(([w]) => w), ...OS_WORDS.flatMap(([w]) => w), ...SIGNAL_WORDS.flatMap(([w]) => w), 'installed',
  ...KIND_WORDS.map(([w]) => w), ...CONTACT, ...QUIET, 'never', 'stopped']);

export function interpret(input: string, catalog: Catalog): Interpretation {
  const result: Interpretation = {
    input, readings: [], skipped: [], understood: false, groupBy: null,
    filters: { q: '', kind: 'all', org: [], site: [], os: [], status: [], seen: null, sort: null, signal: [], software: '' },
  };
  const trimmed = input.trim();
  const raw = trimmed.split(/[\s,;!?()"“”]+/).filter(Boolean);
  // A single hostname/serial-like token is an exact lookup, never a sentence.
  if (!trimmed || uuidPattern.test(trimmed) || (raw.length === 1 && /\d|[-_.]/.test(raw[0]!))) {
    result.filters.q = trimmed;
    return result;
  }
  const tokens: Token[] = [];
  let after = 0;
  for (const m of trimmed.matchAll(/[^\s,;!?()"“”]+/g)) {
    const norm = normalize(m[0]);
    if (!norm) continue;
    tokens.push({ raw: m[0], norm, used: false, pause: /[,;!?]/.test(trimmed.slice(after, m.index)) });
    after = m.index + m[0].length;
  }
  const free = (i: number, n: number) => i >= 0 && i + n <= tokens.length && tokens.slice(i, i + n).every(t => !t.used);
  const matches = (i: number, words: string[]) => free(i, words.length) && words.every((w, k) => tokens[i + k]!.norm === w);
  const take = (i: number, n: number) => { for (let k = i; k < i + n; k++) tokens[k]!.used = true; return tokens.slice(i, i + n).map(t => t.raw).join(' '); };
  const read = (text: string, meaning: string, type: Reading['type'] = 'filter') => result.readings.push({ text, meaning, type });
  const kinds = new Set<RecordKind>();

  let unsupported = matchEntities(tokens, catalog, result, { free, take, read });

  // Check-in time. Negated contact ("haven't checked in this week") means older
  // than the span, and includes devices with no check-in at all.
  // The verb must be in the same clause with nothing readable between: in "not offline and checked in today" the negation is offline's.
  const clauseEnds = (k: number) => tokens[k]!.pause || ['and', 'but', 'or'].includes(tokens[k]!.norm) || PHRASES.some(w => matches(k, w));
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.used) continue;
    const negated = NEGATION.has(t.norm) || t.norm.endsWith("n't");
    const never = t.norm === 'never';
    if (!negated && !never) continue;
    if (PHRASES.some(w => matches(i, w))) continue; // "without antivirus", "not patched": the negation is the phrase's own.
    const verbAt = findAhead(tokens, i + 1, 4, k => CONTACT.has(k.norm), clauseEnds);
    if (verbAt < 0) continue;
    const span = parseSpan(tokens, verbAt + 1, 7);
    const end = span ? span.end : skipParticles(tokens, verbAt + 1);
    const text = take(i, end - i);
    if (never && !span) { result.filters.seen = { type: 'never' }; read(text, 'Never checked in'); }
    else {
      // "Not online" pairs with "online" (an hour); other phrasings default to a week.
      const online = tokens[verbAt]!.norm === 'online';
      const amount = span?.amount ?? (online ? 1 : 7), unit = span?.unit ?? (online ? 'h' : 'd');
      result.filters.seen = { type: 'older', amount, unit, includeNever: true };
      read(text, `No check-in for ${spanLabel(amount, unit)}, or never${span ? '' : ` (assumed ${spanLabel(amount, unit)})`}`);
    }
  }
  for (let i = 0; i < tokens.length && !result.filters.seen; i++) {
    const t = tokens[i]!;
    if (t.used) continue;
    const quiet = QUIET.includes(t.norm);
    const stopped = t.norm === 'stopped' && findAhead(tokens, i + 1, 2, k => CONTACT.has(k.norm) || k.norm === 'responding') >= 0;
    if (quiet || stopped) {
      const after = stopped ? findAhead(tokens, i + 1, 2, k => CONTACT.has(k.norm) || k.norm === 'responding') + 1 : i + 1;
      const span = parseSpan(tokens, skipParticles(tokens, after), 5);
      const amount = span?.amount ?? 7, unit = span?.unit ?? 'd';
      const text = take(i, (span ? span.end : skipParticles(tokens, after)) - i);
      result.filters.seen = { type: 'older', amount, unit, includeNever: true };
      read(text, `No check-in for ${spanLabel(amount, unit)}, or never${span ? '' : ` (assumed ${spanLabel(amount, unit)})`}`);
      continue;
    }
    if (CONTACT.has(t.norm) || t.norm === 'recently') {
      const span = parseSpan(tokens, i + 1, 6);
      if (span) {
        // "Checked in 7 days ago" or "more than 7 days ago" is the far side of the boundary; "less than 2 days ago" the near side.
        const ago = tokens[span.end]?.norm === 'ago' && !tokens[span.end]!.used;
        const words = tokens.slice(i + 1, span.end).map(k => k.norm);
        const nearer = words.some(w => ['less', 'under', 'fewer'].includes(w));
        // "More than a week", "at least 3 days", "over a week" are the far side; "over the last week" means during it.
        const over = words.indexOf('over'), during = over >= 0 && ['the', 'last', 'past'].includes(words[over + 1] ?? '');
        const farther = words.includes('more') || words.includes('least') || (over >= 0 && !during);
        const text = take(i, (ago ? span.end + 1 : span.end) - i);
        if ((ago || farther) && !nearer) {
          result.filters.seen = { type: 'older', amount: span.amount, unit: span.unit, includeNever: false };
          read(text, `Last check-in more than ${spanLabel(span.amount, span.unit)} ago`);
        } else {
          result.filters.seen = { type: 'within', amount: span.amount, unit: span.unit };
          read(text, `Checked in within ${spanLabel(span.amount, span.unit)}`);
        }
      } else if (t.norm === 'online' || t.norm === 'recently') {
        take(i, 1);
        result.filters.seen = t.norm === 'online' ? { type: 'within', amount: 1, unit: 'h' } : { type: 'within', amount: 1, unit: 'd' };
        read(t.raw, t.norm === 'online' ? 'Checked in within 1 hour (closest recorded signal to “online”)' : 'Checked in within 24 hours');
      }
    }
  }
  // "offline for 3 days": a duration directly after a status word.
  for (let i = 0; i < tokens.length && !result.filters.seen; i++) {
    if (!['offline', 'down', 'disconnected', 'unreachable'].includes(tokens[i]!.norm)) continue;
    const span = parseSpan(tokens, i + 1, 5);
    if (!span) continue;
    const text = take(i + 1, span.end - i - 1);
    result.filters.seen = { type: 'older', amount: span.amount, unit: span.unit, includeNever: false };
    read(text, `No check-in for ${spanLabel(span.amount, span.unit)}`);
  }

  // "not offline", "without problems", "aren't Windows": a negated status or OS means its complement,
  // never the thing negated. Only an unused negation directly before (or one filler word before) counts.
  const negatedFrom = (i: number) => {
    const at = (k: number) => (k >= 0 && !tokens[k]!.used ? tokens[k]!.norm : '');
    // Up to two filler words may separate them: "isn't currently a Windows device".
    for (let k = i - 1; k >= i - 3; k--) {
      if (NEGATION.has(at(k)) || at(k) === 'non' || at(k) === 'never') return k;
      if (!NEGATION_FILLER.has(at(k))) return -1;
    }
    return -1;
  };
  // Inventory keeps only the current state, so "never offline" can't be answered either way: refuse, don't complement.
  const history = (from: number, end: number, what: string) => {
    if (tokens[from]!.norm !== 'never') return false;
    result.skipped.push({ text: take(from, end - from), reason: `History isn’t recorded yet — only the current ${what}` });
    unsupported = true;
    return true;
  };
  // Search can't yet ask for the absence of a health issue or a program: refuse rather than invert.
  for (const [words, keys, meaning] of SIGNAL_WORDS) {
    for (let i = 0; i < tokens.length; i++) {
      if (!matches(i, words)) continue;
      const from = negatedFrom(i);
      if (from >= 0 && history(from, i + words.length, 'health signals')) continue;
      if (from >= 0) {
        result.skipped.push({ text: take(from, i + words.length - from), reason: 'Can’t search for devices without a health issue yet' });
        unsupported = true;
        continue;
      }
      const text = take(i, words.length);
      for (const k of keys) if (!result.filters.signal.includes(k)) result.filters.signal.push(k);
      read(text, meaning);
    }
  }
  // "devices with Zoom installed", "7-zip installed": names before the word installed.
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i]!.norm !== 'installed' || tokens[i]!.used || result.filters.software) continue;
    let start = i;
    while (start > 0 && i - start < 3 && !tokens[start - 1]!.used
      && !NEGATION.has(tokens[start - 1]!.norm)
      && !['with', 'has', 'have', 'having', 'that', 'which', 'where', 'running', 'using', 'devices', 'device', 'computers', 'machines', 'never'].includes(tokens[start - 1]!.norm)) start--;
    if (start === i) continue;
    const name = tokens.slice(start, i).map(t => t.raw).join(' ');
    if (name.length < 2) continue;
    const before = (k: number) => (start - k >= 0 ? tokens[start - k]!.norm : '');
    if (NEGATION.has(before(1)) || (['have', 'has', 'having'].includes(before(1)) && NEGATION.has(before(2)))) {
      const from = NEGATION.has(before(1)) ? start - 1 : start - 2;
      result.skipped.push({ text: take(from, i - from + 1), reason: 'Can’t search for devices without a program yet' });
      unsupported = true;
      continue;
    }
    take(start, i - start + 1);
    if (start > 0 && ['with', 'has', 'have', 'having'].includes(tokens[start - 1]!.norm)) tokens[start - 1]!.used = true;
    result.filters.software = name.slice(0, 100);
    read(`${name} installed`, `Has software matching “${name}”`);
  }
  const notStatus: StatusValue[] = [], notOs: OsValue[] = [];
  for (const [words, values, meaning] of STATUS_WORDS) {
    for (let i = 0; i < tokens.length; i++) {
      if (!matches(i, words)) continue;
      const from = negatedFrom(i);
      if (from >= 0 && history(from, i + words.length, 'status')) continue;
      if (from >= 0) {
        const text = take(from, i + words.length - from);
        notStatus.push(...values);
        read(text, `Status isn’t ${values.map(v => STATUS_LABEL[v]).join(' or ')}`);
        continue;
      }
      const text = take(i, words.length);
      for (const v of values) if (!result.filters.status.includes(v)) result.filters.status.push(v);
      read(text, meaning);
    }
  }
  for (const [words, os, note] of OS_WORDS) {
    for (let i = 0; i < tokens.length; i++) {
      if (!matches(i, words)) continue;
      const from = negatedFrom(i);
      if (from >= 0 && history(from, i + words.length, 'OS')) continue;
      if (from >= 0) {
        notOs.push(os);
        read(take(from, i + words.length - from), `OS isn’t ${OS_LABEL[os]}`);
        continue;
      }
      const text = take(i, words.length);
      if (!result.filters.os.includes(os)) result.filters.os.push(os);
      read(text, `OS is ${OS_LABEL[os]}`);
      if (note) result.skipped.push({ text, reason: note });
    }
  }
  // Positive and negated together narrow ("offline, not attention"); a negation alone takes the rest.
  let contradiction = false;
  if (notStatus.length) {
    const kept = (result.filters.status.length ? result.filters.status : [...statusValues]).filter(v => !notStatus.includes(v));
    contradiction ||= !kept.length;
    result.filters.status = kept;
  }
  if (notOs.length) {
    const kept = (result.filters.os.length ? result.filters.os : [...osValues]).filter(v => !notOs.includes(v));
    contradiction ||= !kept.length;
    result.filters.os = kept;
  }
  for (const [word, kind, note] of KIND_WORDS) {
    for (let i = 0; i < tokens.length; i++) {
      if (!matches(i, [word])) continue;
      take(i, 1);
      kinds.add(kind);
      if (note) result.skipped.push({ text: tokens[i]!.raw, reason: note });
    }
  }
  const sorts: Array<[string[], Sort, string]> = [
    [['least', 'recently'], { field: 'lastSeenAt', direction: 'asc' }, 'Oldest check-in first'],
    [['most', 'recently'], { field: 'lastSeenAt', direction: 'desc' }, 'Most recent check-in first'],
    [['most', 'recent'], { field: 'lastSeenAt', direction: 'desc' }, 'Most recent check-in first'],
    [['most', 'urgent'], { field: 'status', direction: 'asc' }, 'Most severe status first'],
    [['most', 'severe'], { field: 'status', direction: 'asc' }, 'Most severe status first'],
    [['by', 'name'], { field: 'title', direction: 'asc' }, 'Sorted by name'],
    [['oldest'], { field: 'lastSeenAt', direction: 'asc' }, 'Oldest check-in first'],
    [['longest'], { field: 'lastSeenAt', direction: 'asc' }, 'Oldest check-in first'],
    [['newest'], { field: 'lastSeenAt', direction: 'desc' }, 'Most recent check-in first'],
    [['latest'], { field: 'lastSeenAt', direction: 'desc' }, 'Most recent check-in first'],
    [['worst'], { field: 'status', direction: 'asc' }, 'Most severe status first'],
    [['alphabetically'], { field: 'title', direction: 'asc' }, 'Sorted by name'],
  ];
  for (const [words, sort, meaning] of sorts) {
    for (let i = 0; i < tokens.length; i++) {
      if (!matches(i, words) || result.filters.sort) continue;
      read(take(i, words.length), meaning, 'sort');
      result.filters.sort = sort;
    }
  }

  const deviceFilters = result.filters.os.length > 0 || result.filters.status.length > 0 || result.filters.seen !== null
    || result.filters.signal.length > 0 || !!result.filters.software
    || result.filters.sort?.field === 'lastSeenAt' || result.filters.sort?.field === 'status';
  if (deviceFilters) {
    kinds.delete('device');
    // "Which customers have offline devices?" — filter devices, roll up by customer.
    if (kinds.has('organization')) result.groupBy = 'organization';
    else if (kinds.has('site')) result.groupBy = 'site';
    result.filters.kind = 'device';
  } else if (kinds.size === 1) {
    result.filters.kind = [...kinds][0]!;
  } else if (kinds.has('device')) {
    result.filters.kind = 'device';
    if (kinds.has('organization')) result.groupBy = 'organization';
    else if (kinds.has('site')) result.groupBy = 'site';
  }
  if (result.filters.kind === 'organization' && result.filters.site.length) result.filters.kind = 'all';

  const identifiers: string[] = [];
  for (const [i, t] of tokens.entries()) {
    if (t.used) continue;
    const refusal = unplaced(tokens, i);
    if (refusal) {
      result.skipped.push({ text: t.raw, reason: refusal });
      unsupported = true;
    } else if (STOP.has(t.norm) || t.norm.length < 2) continue;
    else if (looksLikeIdentifier(t.raw)) identifiers.push(t.raw);
    else result.skipped.push({ text: t.raw, reason: 'Not a field inventory records — it wasn’t used' });
  }
  result.filters.q = identifiers.join(' ');
  result.understood = !contradiction && !unsupported && (result.readings.length > 0 || result.filters.kind !== 'all');
  if (contradiction) result.skipped.push({ text: input.trim(), reason: 'These conditions rule each other out, so nothing could match' });
  return result;
}

/**
 * Why a negation, exclusion or "never" that nothing could place stops the question, or null. Dropping it would
 * brief the opposite or a wider set: "devices that don't belong to Acme", "except the ones at Acme", "never ran Windows".
 */
function unplaced(tokens: Token[], i: number) {
  const w = tokens[i]!.norm;
  if (w === 'never') return 'History isn’t recorded yet — only the current inventory';
  if (EXCLUSION.has(w) || w === 'outside' || (EXCLUSION_PAIRS.has(w) && EXCLUSION_PAIRS.get(w) === tokens[i + 1]?.norm) || excludingBut(tokens, i)) {
    return 'Can’t exclude records from a search yet';
  }
  if (NEGATION.has(w) || w.endsWith("n't") || w === 'non') return 'Can’t tell what this negation applies to, so nothing was searched';
  return null;
}

interface Helpers {
  free: (i: number, n: number) => boolean;
  take: (i: number, n: number) => string;
  read: (text: string, meaning: string, type?: Reading['type']) => void;
}

/** Start of a negation or exclusion directly before a customer or site ("not at", "except for the", "other than"), or -1. */
function excludedFrom(tokens: Token[], i: number) {
  let preposition = false;
  for (let k = i - 1; k >= 0 && k >= i - 4; k--) {
    const t = tokens[k]!;
    if (t.used) return -1;
    // "Everything but Acme" and "all devices but at Acme" exclude; in "offline but at Acme" the "but" only joins two conditions.
    if (t.norm === 'but') return preposition && joiningBut(tokens, k) ? -1 : k;
    if (NEGATION.has(t.norm) || t.norm.endsWith("n't") || ['non', 'outside'].includes(t.norm) || EXCLUSION.has(t.norm)
      || EXCLUSION_PAIRS.get(t.norm) === tokens[k + 1]!.norm) return k;
    // Only a preposition or article-like filler may sit between: in "not reporting at Acme" the negation is the check-in's.
    if (PREPOSITIONS.has(t.norm)) preposition = true;
    else if (t.norm !== 'than' && !(NEGATION_FILLER.has(t.norm) && !CONTACT.has(t.norm))) return -1;
  }
  return -1;
}

/** Customers and sites, by full name first, then by an unambiguous leading part of a customer name. Returns true when one was refused. */
function matchEntities(tokens: Token[], catalog: Catalog, result: Interpretation, { free, take, read }: Helpers) {
  type Entry = { words: string[]; orgs: Catalog['organizations']; sites: Catalog['sites'] };
  const byTitle = new Map<string, Entry>();
  const entry = (title: string) => {
    const words = titleTokens(title), key = words.join(' ');
    if (!byTitle.has(key)) byTitle.set(key, { words, orgs: [], sites: [] });
    return byTitle.get(key)!;
  };
  catalog.organizations.forEach(o => entry(o.title).orgs.push(o));
  catalog.sites.forEach(s => entry(s.title).sites.push(s));
  const entries = [...byTitle.values()].filter(e => e.words.length).sort((a, b) => b.words.length - a.words.length);
  const orgName = new Map(catalog.organizations.map(o => [o.id, o.title]));
  const siteHits: Array<{ text: string; sites: Catalog['sites'] }> = [];
  let refused = false;

  for (const e of entries) {
    for (let i = 0; i < tokens.length; i++) {
      if (!free(i, e.words.length) || !e.words.every((w, k) => tokens[i + k]!.norm === w)) continue;
      const preposition = i > 0 && PREPOSITIONS.has(tokens[i - 1]!.norm) && !tokens[i - 1]!.used;
      const qualifier = tokens[i + e.words.length] && ['site', 'office', 'location'].includes(tokens[i + e.words.length]!.norm);
      const from = excludedFrom(tokens, i);
      if (from >= 0) {
        const end = i + e.words.length + (qualifier && !e.orgs.length ? 1 : 0);
        result.skipped.push({ text: take(from, end - from), reason: OUTSIDE });
        refused = true;
      } else if (e.orgs.length) {
        const text = take(i, e.words.length);
        for (const o of e.orgs) if (!result.filters.org.includes(o.id)) result.filters.org.push(o.id);
        read(text, `Customer is ${e.orgs.map(o => o.title).join(' or ')}`, 'entity');
      } else if (e.words.length > 1 || preposition || qualifier) {
        // A one-word site name ("Main", "Clinic") is too common to match on its own.
        siteHits.push({ text: take(i, e.words.length), sites: e.sites });
        if (qualifier) tokens[i + e.words.length]!.used = true;
      } else continue;
      if (preposition) tokens[i - 1]!.used = true;
    }
  }
  // Leading words of a customer name ("harbor" → Harbor Dental), when unambiguous.
  for (let i = 0; i < tokens.length; i++) {
    for (let n = 3; n >= 1; n--) {
      if (!free(i, n)) continue;
      const words = tokens.slice(i, i + n).map(t => t.norm);
      if (KEYWORDS.has(words[0]!) || (n === 1 && (words[0]!.length < 4 || STOP.has(words[0]!)))) continue;
      const found = catalog.organizations.filter(o => {
        const title = titleTokens(o.title);
        return title.length > n && words.every((w, k) => title[k] === w);
      });
      if (found.length !== 1 || result.filters.org.includes(found[0]!.id)) continue;
      const from = excludedFrom(tokens, i);
      if (from >= 0) {
        result.skipped.push({ text: take(from, i + n - from), reason: OUTSIDE });
        refused = true;
        break;
      }
      const preposition = i > 0 && PREPOSITIONS.has(tokens[i - 1]!.norm) && !tokens[i - 1]!.used;
      read(take(i, n), `Customer is ${found[0]!.title}`, 'entity');
      if (preposition) tokens[i - 1]!.used = true;
      result.filters.org.push(found[0]!.id);
      break;
    }
  }
  for (const hit of siteHits) {
    const inScope = result.filters.org.length ? hit.sites.filter(s => result.filters.org.includes(s.organizationId)) : hit.sites;
    if (!inScope.length) {
      result.skipped.push({ text: hit.text, reason: `That site belongs to ${hit.sites.map(s => orgName.get(s.organizationId) ?? 'another customer').join(', ')}, not the customer named` });
      continue;
    }
    for (const s of inScope) if (!result.filters.site.includes(s.id)) result.filters.site.push(s.id);
    const owners = inScope.map(s => orgName.get(s.organizationId)).filter(Boolean);
    read(hit.text, `Site is ${inScope[0]!.title}${owners.length ? ` (${owners.join(', ')})` : ''}`, 'entity');
  }
  return refused;
}

function findAhead(tokens: Token[], from: number, within: number, test: (t: Token) => boolean, stop: (i: number) => boolean = () => false) {
  for (let i = from; i < Math.min(tokens.length, from + within); i++) {
    if (tokens[i]!.used || stop(i)) return -1;
    if (test(tokens[i]!)) return i;
  }
  return -1;
}

const PARTICLES = new Set(['in', 'been', 'yet', 'back', 'from', 'up', 'any', 'anything']);
function skipParticles(tokens: Token[], from: number) {
  let i = from;
  while (i < tokens.length && !tokens[i]!.used && PARTICLES.has(tokens[i]!.norm)) i++;
  return i;
}

/** "in the last 3 days", "for over a week", "this week", "today", "24h". Clamped to what a link can carry. */
function parseSpan(tokens: Token[], from: number, within: number): { amount: number; unit: Unit; end: number } | null {
  const span = readSpan(tokens, from, within);
  return span && { ...span, amount: Math.min(span.amount, SEEN_LIMITS[span.unit]) };
}
function readSpan(tokens: Token[], from: number, within: number): { amount: number; unit: Unit; end: number } | null {
  const limit = Math.min(tokens.length, from + within);
  for (let i = from; i < limit; i++) {
    const t = tokens[i]!;
    if (t.used) return null;
    if (t.norm === 'today') return { amount: 1, unit: 'd', end: i + 1 };
    if (t.norm === 'yesterday') return { amount: 1, unit: 'd', end: i + 1 };
    if (t.norm === 'this' && tokens[i + 1]?.norm === 'week') return { amount: 7, unit: 'd', end: i + 2 };
    if (t.norm === 'this' && tokens[i + 1]?.norm === 'month') return { amount: 30, unit: 'd', end: i + 2 };
    const compact = /^(\d{1,3})(h|d|w)$/.exec(t.norm);
    if (compact) {
      const n = Number(compact[1]);
      return compact[2] === 'h' ? { amount: n, unit: 'h', end: i + 1 } : { amount: n * (compact[2] === 'w' ? 7 : 1), unit: 'd', end: i + 1 };
    }
    const number = /^\d{1,3}$/.test(t.norm) ? Number(t.norm) : NUMBER_WORDS[t.norm];
    if ((t.norm === 'a' || t.norm === 'an') && NUMBER_WORDS[tokens[i + 1]?.norm ?? ''] !== undefined) continue; // "a few days"
    if (number !== undefined) {
      let j = i + 1;
      if (tokens[j]?.norm === 'of') j++; // "a couple of days"
      const unit = UNITS[tokens[j]?.norm ?? ''];
      if (unit && number > 0) return { amount: number * unit.factor, unit: unit.unit, end: j + 1 };
    }
    const bare = UNITS[t.norm];
    if (bare && ['week', 'day', 'month', 'hour'].includes(t.norm)) return { amount: bare.factor, unit: bare.unit, end: i + 1 };
    if (!['in', 'for', 'within', 'during', 'over', 'the', 'last', 'past', 'more', 'than', 'less', 'under', 'fewer', 'at', 'least', 'since', 'about', 'almost', 'nearly'].includes(t.norm)) return null;
  }
  return null;
}

const spanLabel = (amount: number, unit: Unit) =>
  unit === 'h' ? `${amount} hour${amount === 1 ? '' : 's'}` : amount === 1 ? '24 hours' : amount % 7 === 0 && amount <= 28
    ? `${amount / 7} week${amount === 7 ? '' : 's'}` : `${amount} days`;

export function applyInterpretation(state: FilterState, interpretation: Interpretation): FilterState {
  const f = interpretation.filters;
  return { ...state, ...f, page: 1, seen: f.seen as SeenFilter | null };
}
